import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import useCurrentShopSuspense from "../hooks/use-current-shop-suspense";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { CreateWorkflowDto, UpdateWorkflowDto, Workflow } from "../types";
import { useWorkflowsEditorConfig } from "./workflows-editor-config-context";
import useFocusNode from "../hooks/use-focus-node";
import { useWorkflowSelectedGroupId } from "./workflow-selected-group-context";
import { duplicateWorkflowWithNewIds } from "../lib/duplicate-workflow";


interface WorkflowsEditorWorkflowsContextType {
  workflows: Workflow[];
  currentWorkflow: Workflow | null;
  addWorkflow: (workflow: OptionalCreateWorkflowWithRequiredTrigger) => Promise<Workflow>;
  updateWorkflow: (updatedWorkflow: UpdateWorkflowDto, id?: string) => Promise<void>;
  removeWorkflow: (workflowId: string) => Promise<void>;
  onWorkflowSelect?: (workflowId?: string) => void;
  /**
   * Returns true (once) if the given workflow id was just created in this
   * session, clearing the flag. Used to open a freshly created workflow
   * directly in edit mode.
   */
  consumeJustCreatedWorkflow: (workflowId: string) => boolean;
  // Use the form if you need to access the current workflow data
}


export type WorkflowsFormData = {
  workflow: Workflow | null;
};

type OptionalCreateWorkflowWithRequiredTrigger = Omit<
  Partial<CreateWorkflowDto>,
  "trigger"
> & {
  trigger: CreateWorkflowDto["trigger"];
};

const WorkflowsEditorWorkflowsContext = createContext<
  WorkflowsEditorWorkflowsContextType | undefined
>(undefined);


export interface WorkflowsEditorWorkflowsProviderProps {
  children: ReactNode;
  currentWorkflowId?: string | null;
  onWorkflowSelect?: (workflowId?: string) => void;
}


function getNewWorkflowTitle(
  existingWorkflows: Workflow[],
  baseTitle?: string,
): string {
  let base = baseTitle || "Nouveau workflow";

  const numberSuffixMatch = base.match(/^(.+?)\s+(\d+)$/);
  if (numberSuffixMatch) {
    base = numberSuffixMatch[1];
  }

  const matchingTitles = existingWorkflows
    .map((t) => t.name)
    .filter((title) => title === base || title.startsWith(`${base} `));

  if (matchingTitles.length === 0) {
    return base;
  }

  const numbers = matchingTitles
    .map((title) => {
      if (title === base) return 1;
      const match = title.match(
        new RegExp(`^${base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")} (\\d+)$`),
      );
      return match ? parseInt(match[1], 10) : null;
    })
    .filter((n): n is number => n !== null);

  const maxNumber = Math.max(...numbers);

  return `${base} ${maxNumber + 1}`;
}


export function WorkflowsEditorWorkflowsProvider({
  children,
  currentWorkflowId,
  onWorkflowSelect,
}: WorkflowsEditorWorkflowsProviderProps) {
  const { data: shop } = useCurrentShopSuspense();
  const { api, createDefaults } = useWorkflowsEditorConfig();
  const { focusNode } = useFocusNode();
  const { setSelectedGroupId } = useWorkflowSelectedGroupId();
  const { data: workflows, refetch: refreshWorkflows } = api.useWorkflows(
    shop.id,
  );
  const form = useForm<WorkflowsFormData>({
    defaultValues: {
      workflow: null,
    },
  });

  const addWorkflowMutation = api.useAddWorkflow(shop.id);
  const updateWorkflowMutation = api.useUpdateWorkflow(shop.id);
  const deleteWorkflowMutation = api.useDeleteWorkflow(shop.id);

  // Id of a workflow just created in this session, so the editor can open it
  // directly in edit mode. Stored in a ref to avoid extra re-renders.
  const justCreatedWorkflowIdRef = useRef<string | null>(null);

  const consumeJustCreatedWorkflow = useCallback((workflowId: string) => {
    if (justCreatedWorkflowIdRef.current === workflowId) {
      justCreatedWorkflowIdRef.current = null;
      return true;
    }
    return false;
  }, []);

  // Derive currentWorkflow from currentWorkflowId
  const currentWorkflow = workflows?.find(w => w.id === currentWorkflowId) || null;

  const handleUpdate = async (data: UpdateWorkflowDto, id = currentWorkflow?.id): Promise<void> => {
    if (!id) return;
    const response = await updateWorkflowMutation.mutateAsync({
      id,
      requestBody: data,
    });

    if (id === currentWorkflow?.id) {
      form.reset({ workflow: response }, { keepDirty: false });
    }
    await refreshWorkflows();
  };

  const handleDelete = async (workflowId: string): Promise<void> => {
    await deleteWorkflowMutation.mutateAsync(workflowId);

    if (workflowId === currentWorkflow?.id) {
      onWorkflowSelect?.(undefined);
    }
    await refreshWorkflows();
  };

  const handleAdd = async (workflow: OptionalCreateWorkflowWithRequiredTrigger): Promise<Workflow> => {
    const name = getNewWorkflowTitle(workflows || [], workflow?.name);
    const { definition, trigger } = duplicateWorkflowWithNewIds(
      workflow as Workflow,
    );
    // createDefaults carries the domain-specific fields (kind/origin for
    // tracking, nothing for return) — the cast covers the domains where
    // those V2 fields don't exist.
    const body = {
      isActive: false,
      isDeletable: true,
      ...createDefaults,
      definition,
      trigger,
      name,
    } as CreateWorkflowDto;

    const response = await addWorkflowMutation.mutateAsync({
      requestBody: body,
    });

    justCreatedWorkflowIdRef.current = response.id;

    if (!currentWorkflow) {
      setTimeout(() => {
        setSelectedGroupId(`workflow-group-${response.id}`);
        focusNode(`workflow-group-${response.id}`, { xOffset: -50 });
      }, 200);
    }

    await refreshWorkflows();
    return response;
  };

  useEffect(() => {
    form.reset({ workflow: currentWorkflow }, { keepDirty: false });
  }, [currentWorkflow]);

  const value: WorkflowsEditorWorkflowsContextType = useMemo(
    () => ({
      workflows: workflows || [],
      currentWorkflow,
      updateWorkflow: handleUpdate,
      addWorkflow: handleAdd,
      removeWorkflow: handleDelete,
      onWorkflowSelect,
      consumeJustCreatedWorkflow,
    }),
    [workflows, currentWorkflow, onWorkflowSelect, consumeJustCreatedWorkflow],
  );

  return (
    <WorkflowsEditorWorkflowsContext.Provider value={value}>
      <FormProvider {...form}>{children}</FormProvider>
    </WorkflowsEditorWorkflowsContext.Provider>
  );
}


export function useWorkflowsEditorWorkflows() {
  const context = useContext(WorkflowsEditorWorkflowsContext);

  if (context === undefined) {
    throw new Error(
      "useWorkflowsEditorWorkflows must be used within a WorkflowsEditorWorkflowsProvider",
    );
  }
  return context;
}


export function useWorkflowForm() {
  const context = useFormContext<WorkflowsFormData>();

  if (context === undefined) {
    throw new Error(
      "useWorkflowsForm must be used within a WorkflowsEditorWorkflowsProvider",
    );
  }
  return context;
}


export function useIsWorkflowsListingMode() {
  const { currentWorkflow } = useWorkflowsEditorWorkflows();

  return !currentWorkflow;
}
